import { concat, get } from '@ember/helper';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismBadge,
  PrismBanner,
  PrismIcon,
  PrismTooltipHeader,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { and, or } from 'ember-truth-helpers';
import GeneralDemandLevel from 'lumen/components/general/demand-level';
import changeColorClass from 'lumen/helpers/change-color-class';
import dayjsFormat from 'lumen/helpers/dayjs-format';
import formatOtbValue from 'lumen/helpers/format-otb-value';
import hasFeature from 'lumen/helpers/has-feature';
import type { Day } from 'lumen/models';
import {
  MI_DEFAULT_DATE_FORMAT,
  MI_SEARCHES_TYPE_FLIGHTS_BUSINESS,
  MI_SEARCHES_TYPE_FLIGHTS_LEISURE,
  MI_SEARCHES_TYPE_HOTELS_BUSINESS,
  MI_SEARCHES_TYPE_HOTELS_LEISURE,
} from 'lumen/utils/constants/general-constants';
import { fetchEventsByVisibilityFlag } from 'lumen/utils/market-insight/market-insight-helpers';

interface HotelMarketInsightTooltipsTooltipSummarySignature {
  Args: {
    day: unknown;
    isDailyTooltip: unknown;
    isMarketInsightFull: unknown;
    rateindex: unknown;
    refDaysAgo: unknown;
  };
}

export default class HotelMarketInsightTooltipsTooltipSummary extends Component<HotelMarketInsightTooltipsTooltipSummarySignature> {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare miSearches: Services['mi-searches'];
  @service declare miSelections: Services['mi-selections'];
  @service declare store: Services['store'];

  // Generates an array containing all days in the selected daterange in a YYYY-MM-DD string format
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get datesToCheckForWeek() {
    const { day } = this.args;
    const dates = [];

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    let start = day.date;
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const end = day.date.endOf('week');

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    while (start.isSameOrBefore(end)) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      dates.push(start.format('YYYY-MM-DD'));
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      start = start.add(1, 'day');
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return dates;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get flightBusinessSearchSummary() {
    const { day, isDailyTooltip } = this.args;

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
    return day.getMostRecentSearchSummary(
      isDailyTooltip,
      MI_SEARCHES_TYPE_FLIGHTS_BUSINESS,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get flightLeisureSearchSummary() {
    const { day, isDailyTooltip } = this.args;

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
    return day.getMostRecentSearchSummary(
      isDailyTooltip,
      MI_SEARCHES_TYPE_FLIGHTS_LEISURE,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasDemandChange() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.summary?.hasDemandChange;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasFlightBusinessSearches() {
    return this.miSearches
      ?.areFlightBusinessSearchesSupportedForTheCurrentDestination;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasFlightLeisureSearches() {
    return this.miSearches
      ?.areFlightLeisureSearchesSupportedForTheCurrentDestination;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasHotelBusinessSearches() {
    return this.miSearches
      ?.areHotelBusinessSearchesSupportedForTheCurrentDestination;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasHotelLeisureSearches() {
    return this.miSearches
      ?.areHotelLeisureSearchesSupportedForTheCurrentDestination;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasMarketOtb() {
    return this.access.hasMarketOtb;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasOTB() {
    return this.access.hasOTB;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasSearches() {
    return (
      this.hasFlightLeisureSearches ||
      this.hasFlightBusinessSearches ||
      this.hasHotelLeisureSearches ||
      this.hasHotelBusinessSearches
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotelsBusinessSearchSummary() {
    const { day, isDailyTooltip } = this.args;

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
    return day.getMostRecentSearchSummary(
      isDailyTooltip,
      MI_SEARCHES_TYPE_HOTELS_BUSINESS,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotelsLeisureSearchSummary() {
    const { day, isDailyTooltip } = this.args;

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
    return day.getMostRecentSearchSummary(
      isDailyTooltip,
      MI_SEARCHES_TYPE_HOTELS_LEISURE,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get marketPickup() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.occupancy?.benchmarkChangeVersusPercentage(
      this.occupancyChange,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get myPickupValues() {
    return {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      myPickup: this.occupancy?.occupancyChangeVersusPercentage(
        this.occupancyChange,
      ),
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      roomsBooked: this.occupancy?.roomsBookedChangeVersus(
        this.occupancyChange,
      ),
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get numberOfEventsAndHolidays() {
    const { day, isDailyTooltip } = this.args;

    let numberOfEvents = 0;
    let numberOfHolidays = 0;

    if (day) {
      if (isDailyTooltip) {
        // We only show accepted events in the tooltip even if the showAllEvents is toggled on from the city map
        // @ts-expect-error: Incorrect type
        numberOfEvents = fetchEventsByVisibilityFlag(day, false).length;
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        numberOfHolidays = day.holidays.length;
      } else {
        // @ts-expect-error: Incorrect type
        const listOfEvents = [];

        this.datesToCheckForWeek.forEach((date) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          const dayModel = this.store.peekRecord<Day>('day', date);

          // @ts-expect-error: Incorrect type
          listOfEvents.push(dayModel.calendarEvents);

          // @ts-expect-error: Incorrect type
          numberOfHolidays += dayModel.holidays.length;
        });

        // remove duplicate events from the list of events i.e. count events spanning multiple days of the same week as one
        // @ts-expect-error: Incorrect type
        const listOfEventsSet = new Set(listOfEvents.flat());
        numberOfEvents = listOfEventsSet.size;
      }
    }

    return {
      events: numberOfEvents,
      holidays: numberOfHolidays,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get occupancy() {
    const { day, isDailyTooltip } = this.args;

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return isDailyTooltip ? day.occupancy : day.weeklyOccupancy;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get occupancyChange() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.occupancy?.occupancyChangeForExtractDate(
      this.occupancyChangeExtractDate,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get occupancyChangeExtractDate() {
    const { refDaysAgo } = this.args;

    // subtract the number of refDaysAgo to get the correct extract date
    return (
      this.hotelAndMarketService
        .getLocalTime()
        // @ts-expect-error: Incorrect type
        .subtract(refDaysAgo, 'day')
        .format('YYYY-MM-DD')
    );
  }

  /**
   * returns the number of opportunities or outliers there are in this week
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get opportunitiesCountText() {
    const { rateindex } = this.args;
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const opportunitiesCount = rateindex ? rateindex.outliersCount : 0;

    if (opportunitiesCount) {
      return `${this.intl.t('market_insight.opportunities')}: ${opportunitiesCount}`;
    }

    return '';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showMarketOTB() {
    return (
      this.hasMarketOtb ||
      (this.access.hasFeature('otb.market_benchmark_eligible') &&
        (this.access.hasFeature('otb.empty_placeholder') ||
          this.access.hasFeature('otb.data_validation')))
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showOTB() {
    return (
      this.hasOTB ||
      this.access.hasFeature('otb.empty_placeholder') ||
      this.access.hasFeature('otb.data_validation')
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get summary() {
    const { day, isDailyTooltip } = this.args;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return isDailyTooltip
      ? // @ts-expect-error: Incorrect type
        day.mostRecentDailySummary
      : // @ts-expect-error: Incorrect type
        day.mostRecentWeeklySummary;
  }

  <template>
    {{! @glint-nocheck }}
    <PrismTooltipHeader>
      {{#if @isDailyTooltip}}
        {{dayjsFormat
          @day.date
          format=this.miSelections.defaultDateFormatWithDOW
        }}
      {{else}}
        {{t
          "general.week_of"
          date=(dayjsFormat @day.date format=MI_DEFAULT_DATE_FORMAT)
        }}
      {{/if}}
    </PrismTooltipHeader>
    {{#if @isMarketInsightFull}}
      {{#if @isDailyTooltip}}
        {{#if @day.outliers.length}}
          <div class="opportunities-banner-container">
            <PrismBanner
              @bannerStyle="warning"
              @bannerTitle={{get @day.outliers "0.formattedValueString.short"}}
            />
          </div>
        {{/if}}
      {{else}}
        {{#if this.opportunitiesCountText}}
          <div class="opportunities-banner-container">
            <PrismBanner
              @bannerStyle="warning"
              @bannerTitle={{this.opportunitiesCountText}}
              @position="top"
            />
          </div>
        {{/if}}
      {{/if}}
    {{/if}}
    <div class="default-table-tooltip tooltip__table-container">
      <table class="default-table">
        <thead>
          <tr>
            <th class="table-container-small-col"></th>
            <th class="text-200--bold table-container-small-col">{{t
                "rates.occupancy.current"
              }}</th>
            <th
              class="text-200--bold"
              data-test-id="mi-demand-forecast-chart-tooltip-daysago"
            >{{t "general.daysago" count=@refDaysAgo}}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{{t "market_insight.submarket_demand"}}</td>
            <td>
              <GeneralDemandLevel
                @demandLevel={{this.summary.demandLevelColorScaleNumber}}
                @size="small"
              />
            </td>
            <td>
              {{#if this.hasDemandChange}}
                <div class="flex flex-row items-center gap-100">
                  <GeneralDemandLevel
                    @demandLevel={{this.summary.demandLevelColorScaleNumberRefPeriod}}
                    @size="small"
                  />
                  <PrismIcon @name="chevron-right" @size="small" />
                  <GeneralDemandLevel
                    @demandLevel={{this.summary.demandLevelColorScaleNumber}}
                    @size="small"
                  />
                </div>
              {{else}}
                {{t "overview_dashboard.kpi.no_change"}}
              {{/if}}
            </td>
          </tr>

          {{#if this.showOTB}}
            <tr>
              <td class="truncate">
                <span
                  class={{if
                    (hasFeature "otb.empty_placeholder")
                    "text-brand font-bold"
                  }}
                >
                  {{t "rates.my_OTB"}}
                </span>
              </td>
              <td>
                <span
                  class={{if
                    (hasFeature "otb.empty_placeholder")
                    "text-brand font-bold"
                  }}
                >
                  {{#if @isDailyTooltip}}
                    {{formatOtbValue
                      @day.occupancy.occupancyPercentage
                      signDisplay="always"
                      style="percent"
                    }}
                    ({{formatOtbValue
                      @day.occupancy.roomsBooked
                    }}/{{formatOtbValue @day.occupancy.totalCapacity}})
                  {{else}}
                    {{formatOtbValue
                      @day.weeklyOccupancy.occupancyPercentage
                      signDisplay="always"
                      style="percent"
                    }}
                    ({{formatOtbValue
                      @day.weeklyOccupancy.roomsBooked
                    }}/{{formatOtbValue @day.weeklyOccupancy.totalCapacity}})
                  {{/if}}
                </span>
              </td>
              <td>
                <span
                  class="{{changeColorClass this.myPickupValues.roomsBooked}}
                    {{if
                      (hasFeature 'otb.empty_placeholder')
                      'text-brand font-bold'
                    }}"
                >
                  {{formatOtbValue
                    this.myPickupValues.myPickup
                    style="percent"
                  }}
                  ({{formatOtbValue
                    this.myPickupValues.roomsBooked
                    signDisplay="always"
                  }})
                </span>
              </td>
            </tr>
          {{/if}}
          {{#if this.showMarketOTB}}
            <tr>
              <td class="truncate">
                <span
                  class={{if
                    (hasFeature "otb.empty_placeholder")
                    "text-brand font-bold"
                  }}
                >
                  {{t "rates.tooltip.market_otb"}}
                </span>
              </td>
              <td>
                <span
                  class={{if
                    (hasFeature "otb.empty_placeholder")
                    "text-brand font-bold"
                  }}
                >
                  {{#if @isDailyTooltip}}
                    {{@day.occupancy.formattedBenchmarkPercentage}}
                  {{else}}
                    {{@day.weeklyOccupancy.formattedBenchmarkPercentage}}
                  {{/if}}
                </span>
              </td>
              <td>
                <div
                  class="{{changeColorClass this.marketPickup}}
                    {{if
                      (hasFeature 'otb.empty_placeholder')
                      'text-brand font-bold'
                    }}"
                >
                  {{formatOtbValue this.marketPickup style="percent"}}
                </div>
              </td>
            </tr>
          {{/if}}

          {{#if (and @isMarketInsightFull @isDailyTooltip)}}
            <tr>
              <td class="truncate">{{t "market_insight.my_price"}}</td>
              <td>
                <div class="flex items-center gap-100">
                  {{@rateindex.formattedRate}}
                  {{#if @rateindex.formattedCancellableIcon}}
                    <PrismIcon
                      @name={{@rateindex.formattedCancellableIcon}}
                      @size="extra-small"
                    />
                  {{/if}}
                </div>
              </td>
              <td></td>
            </tr>
          {{/if}}
        </tbody>
      </table>
    </div>

    {{#if
      (or
        this.numberOfEventsAndHolidays.events
        this.numberOfEventsAndHolidays.holidays
      )
    }}
      <div
        class="events-and-holidays-container flex flex-row
          {{unless this.hasSearches 'mb-300'}}"
      >
        {{#if this.numberOfEventsAndHolidays.events}}
          <div class="mr-200">
            <PrismBadge
              @iconName="calendar-accepted"
              @iconPosition="left"
              @label={{t
                "market_insight.demand_forecast_graph.tooltip.accepted_events"
                count=this.numberOfEventsAndHolidays.events
              }}
            />
          </div>
        {{/if}}
        {{#if this.numberOfEventsAndHolidays.holidays}}
          <PrismBadge
            @iconName="events-global"
            @iconPosition="left"
            @label={{t
              "market_insight.detail_pane_section_title.holidays"
              count=this.numberOfEventsAndHolidays.holidays
            }}
          />
        {{/if}}
      </div>
    {{/if}}

    {{#if this.hasSearches}}
      <div class="searches-container">
        <p
          class="text-200--regular"
          data-test-id="mi-demand-forecast-chart-tooltip-searches-title"
        >{{t "market_insight.searches_in_the_last_days" count=7}}</p>
        {{! Hotels GDS (business) AND Flight GDS (business) - Split data for both searches types }}
        {{#if
          (and this.hasFlightBusinessSearches this.hasHotelBusinessSearches)
        }}
          <div class="row-with-badge mt-200 flex items-center">
            <div class="mr-200">
              <PrismIcon @name="plane" @size="small" />
            </div>
            {{#if this.hasFlightLeisureSearches}}
              <div class="mr-100">
                <PrismBadge
                  @badgeType={{this.flightLeisureSearchSummary.numSearchesAsBadgeClass}}
                  @label={{concat
                    (t "general.meta")
                    "/"
                    (t "general.ota")
                    ": "
                    this.flightLeisureSearchSummary.numSearchesAsLabel
                  }}
                />
              </div>
            {{/if}}

            <PrismBadge
              @badgeType={{this.flightBusinessSearchSummary.numSearchesAsBadgeClass}}
              @label={{concat
                (t "general.GDS")
                ": "
                this.flightBusinessSearchSummary.numSearchesAsLabel
              }}
            />
          </div>

          <div class="row-with-badge mt-200 flex items-center">
            <div class="mr-200">
              <PrismIcon @name="business-type-hotel" @size="small" />
            </div>
            {{#if this.hasHotelLeisureSearches}}
              <div class="mr-100">
                <PrismBadge
                  @badgeType={{this.hotelsLeisureSearchSummary.numSearchesAsBadgeClass}}
                  @label={{concat
                    (t "general.meta")
                    "/"
                    (t "general.ota")
                    ": "
                    this.hotelsLeisureSearchSummary.numSearchesAsLabel
                  }}
                />
              </div>
            {{/if}}

            <PrismBadge
              @badgeType={{this.hotelsBusinessSearchSummary.numSearchesAsBadgeClass}}
              @label={{concat
                (t "general.GDS")
                ": "
                this.hotelsBusinessSearchSummary.numSearchesAsLabel
              }}
            />
          </div>
        {{else if this.hasHotelBusinessSearches}}
          {{! Only Hotel GDS (business) - split data only for hotels searches }}
          {{#if this.hasFlightLeisureSearches}}
            <div class="row-with-badge mt-200 flex items-center">
              <div class="mr-200">
                <PrismIcon @name="plane" @size="small" />
              </div>
              <div class="mr-100">
                <PrismBadge
                  @badgeType={{this.flightLeisureSearchSummary.numSearchesAsBadgeClass}}
                  @label={{concat
                    (t "general.meta")
                    "/"
                    (t "general.ota")
                    "/"
                    (t "general.GDS")
                    ": "
                    this.flightLeisureSearchSummary.numSearchesAsLabel
                  }}
                />
              </div>
            </div>
          {{/if}}

          <div class="row-with-badge mt-200 flex items-center">
            <div class="mr-200">
              <PrismIcon @name="business-type-hotel" @size="small" />
            </div>
            {{#if this.hasHotelLeisureSearches}}
              <div class="mr-100">
                <PrismBadge
                  @badgeType={{this.hotelsLeisureSearchSummary.numSearchesAsBadgeClass}}
                  @label={{concat
                    (t "general.meta")
                    "/"
                    (t "general.ota")
                    ": "
                    this.hotelsLeisureSearchSummary.numSearchesAsLabel
                  }}
                />
              </div>
            {{/if}}
            <PrismBadge
              @badgeType={{this.hotelsBusinessSearchSummary.numSearchesAsBadgeClass}}
              @label={{concat
                (t "general.GDS")
                ": "
                this.hotelsBusinessSearchSummary.numSearchesAsLabel
              }}
            />
          </div>
        {{else if this.hasFlightBusinessSearches}}
          {{! Only Flight GDS (business) - split data only for flights searches }}
          <div class="row-with-badge mt-200 flex items-center">
            <div class="mr-200">
              <PrismIcon @name="plane" @size="small" />
            </div>
            {{#if this.hasFlightLeisureSearches}}
              <div class="mr-100">
                <PrismBadge
                  @badgeType={{this.flightLeisureSearchSummary.numSearchesAsBadgeClass}}
                  @label={{concat
                    (t "general.meta")
                    "/"
                    (t "general.ota")
                    ": "
                    this.flightLeisureSearchSummary.numSearchesAsLabel
                  }}
                />
              </div>
            {{/if}}

            <div class="mr-100">
              <PrismBadge
                @badgeType={{this.flightBusinessSearchSummary.numSearchesAsBadgeClass}}
                @label={{concat
                  (t "general.GDS")
                  ": "
                  this.flightBusinessSearchSummary.numSearchesAsLabel
                }}
              />
            </div>
          </div>

          {{#if this.hasHotelLeisureSearches}}
            <div class="row-with-badge mt-200 flex items-center">
              <div class="mr-200">
                <PrismIcon @name="business-type-hotel" @size="small" />
              </div>
              <PrismBadge
                @badgeType={{this.hotelsLeisureSearchSummary.numSearchesAsBadgeClass}}
                @label={{concat
                  (t "general.meta")
                  "/"
                  (t "general.ota")
                  "/"
                  (t "general.GDS")
                  ": "
                  this.hotelsLeisureSearchSummary.numSearchesAsLabel
                }}
              />
            </div>
          {{/if}}
        {{else}}
          {{! No Hotel or Flight GDS data }}
          <div class="row-with-badge mt-200 flex flex-row">
            {{#if this.hasFlightLeisureSearches}}
              <div class="mr-100">
                <PrismBadge
                  @badgeType={{this.flightLeisureSearchSummary.numSearchesAsBadgeClass}}
                  @iconName="plane"
                  @iconPosition="left"
                  @label={{this.flightLeisureSearchSummary.numSearchesAsLabel}}
                />
              </div>
            {{/if}}

            {{#if this.hasHotelLeisureSearches}}
              <PrismBadge
                @badgeType={{this.hotelsLeisureSearchSummary.numSearchesAsBadgeClass}}
                @iconName="business-type-hotel"
                @iconPosition="left"
                @label={{this.hotelsLeisureSearchSummary.numSearchesAsLabel}}
              />
            {{/if}}
          </div>
        {{/if}}
      </div>
    {{/if}}
  </template>
}
